/**
 * Configures the two generated API clients. Imported for its side effects
 * first thing in index.tsx.
 *
 *  - `portalClient` (src/client/portal): the claims-portal API, same origin
 *    under /api (proxied to the portal in dev, by the ingress in production).
 *  - `claimsClient` (src/client/claims): the partners' claims-server APIs.
 *    There is one per partner, so every call passes
 *    `baseUrl: partner.apiBaseUrl` (see `partnerApi` below). The generated
 *    default, the spec's relative `/claims-api`, is meaningless here, so a
 *    call that forgets it is rejected before it is sent instead of silently
 *    fetching same-origin HTML.
 *
 * Both send the Cognito id token as Bearer -- the portal verifies against the
 * id token's audience, so this must stay the id token, not the access token
 * the webapp uses.
 */
import { fetchAuthSession } from 'aws-amplify/auth';

import { portalApiEndpoint } from '../awsConfig';
import { client as claimsClient } from '../client/claims/client.gen';
import { client as portalClient } from '../client/portal/client.gen';

portalClient.setConfig({ baseUrl: portalApiEndpoint });

const authorize = async (request: Request): Promise<Request> => {
  const idToken = (await fetchAuthSession()).tokens?.idToken?.toString();
  if (idToken) {
    request.headers.set('Authorization', `Bearer ${idToken}`);
  }
  return request;
};

const requirePartnerBaseUrl = (request: Request): Request => {
  if (!/^https?:\/\//.test(request.url)) {
    throw new Error(
      `claims API call without partnerApi(...): ${request.url}`
    );
  }
  return request;
};

portalClient.interceptors.request.use(authorize);
claimsClient.interceptors.request.use(requirePartnerBaseUrl);
claimsClient.interceptors.request.use(authorize);

/** Per-request options that point a claims-client call at one partner. */
export const partnerApi = (apiBaseUrl: string) => ({ baseUrl: apiBaseUrl });

export { claimsClient, portalClient };
